import { fetchJSON as fetchAPI } from './client';

export type ProductGridRow = {
  id: string;
  asin?: string;
  sku?: string;
  title?: string;
  brand?: string;
  status: string;
  quality_score: number;
  ai_ready: boolean;
  image_url?: string;
  marketplace_id?: string;
  updated_at: string;
};

export type DashboardSummary = {
  tenant_id: string;
  workspace_id?: string;
  total_products: number;
  active_products: number;
  draft_products: number;
  ai_ready_products: number;
  avg_quality_score: number;
  variation_families: number;
  pending_bulk_jobs: number;
  unread_notifications: number;
  index_health: { indexed_products: number; healthy: boolean; pending_reindex?: number };
  recent_activity?: ActivityEntry[];
  top_products?: ProductGridRow[];
  generated_at: string;
};

export type CatalogHealth = {
  overall_health: number;
  content_completeness: number;
  image_completeness: number;
  attribute_completeness: number;
  compliance_rate: number;
  issues_by_category: Record<string, number>;
  top_issues?: { product_id: string; code: string; message: string; severity: string }[];
};

export type Product = {
  id: string;
  workspace_id: string;
  identifiers: { asin?: string; sku?: string; parent_asin?: string };
  brand: { brand?: string };
  category: { category_id?: string; product_type?: string };
  content: { title?: string; bullets?: string[]; description?: string };
  media?: { id: string; type: string; url?: string; position: number }[];
  attributes?: { key: string; value: unknown }[];
  quality: { overall_score: number; ai_ready: boolean };
  status: string;
  visibility: string;
  marketplace_id?: string;
  is_variation: boolean;
  parent_id?: string;
  child_ids?: string[];
  updated_at: string;
};

export type SearchResult = {
  products: Product[];
  total: number;
  limit: number;
  offset: number;
  facets?: {
    status?: Record<string, number>;
    brand?: Record<string, number>;
    marketplace?: Record<string, number>;
  };
  took_ms?: number;
};

export type ActivityEntry = {
  id: string;
  product_id?: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type AIRecommendation = {
  id: string;
  product_id: string;
  type: string;
  title: string;
  description: string;
  impact: number;
  confidence: number;
  field?: string;
};

export type RelationshipGraph = {
  product_id: string;
  relationships: { source_id: string; target_id: string; type: string; label?: string }[];
  nodes: { product_id: string; title?: string; asin?: string; type: string }[];
};

export type BulkOperation = {
  id: string;
  action: string;
  state: string;
  product_ids: string[];
  progress: number;
  processed: number;
  failed: number;
  total: number;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_AMAZON_CATALOG_API ?? '/api/v1/amazon/catalog';

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const amazonCatalogApi = {
  dashboard: (workspaceId?: string) =>
    fetchJSON<DashboardSummary>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  catalogHealth: (workspaceId?: string) =>
    fetchJSON<CatalogHealth>(`/health${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  productGrid: (workspaceId?: string, limit = 50, offset = 0) =>
    fetchJSON<{ rows: ProductGridRow[]; total: number }>(
      `/grid?workspace_id=${workspaceId ?? ''}&limit=${limit}&offset=${offset}`,
    ),
  globalSearch: (text: string, workspaceId?: string) =>
    fetchJSON<SearchResult>('/search/global', {
      method: 'POST',
      body: JSON.stringify({ text, workspace_id: workspaceId ?? '', limit: 50 }),
    }),
  advancedSearch: (query: Record<string, unknown>) =>
    fetchJSON<SearchResult>('/search/advanced', { method: 'POST', body: JSON.stringify(query) }),
  getProduct: (id: string) => fetchJSON<Product>(`/products/${id}`),
  createProduct: (body: Record<string, unknown>) =>
    fetchJSON<Product>('/products', { method: 'POST', body: JSON.stringify(body) }),
  updateProduct: (id: string, body: Record<string, unknown>) =>
    fetchJSON<Product>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  publishProduct: (id: string) =>
    fetchJSON<Product>(`/products/${id}/publish`, { method: 'POST' }),
  recommendations: (id: string) =>
    fetchJSON<AIRecommendation[]>(`/products/${id}/recommendations`),
  relationships: (id: string) =>
    fetchJSON<RelationshipGraph>(`/products/${id}/relationships`),
  updateMedia: (id: string, media: Product['media']) =>
    fetchJSON<Product>(`/products/${id}/media`, { method: 'PUT', body: JSON.stringify({ media }) }),
  updateAttributes: (id: string, attributes: Product['attributes']) =>
    fetchJSON<Product>(`/products/${id}/attributes`, {
      method: 'PUT',
      body: JSON.stringify({ attributes }),
    }),
  triggerBulk: (body: { action: string; product_ids: string[]; workspace_id: string; parameters?: Record<string, unknown> }) =>
    fetchJSON<BulkOperation>('/bulk', { method: 'POST', body: JSON.stringify(body) }),
  listBulk: (workspaceId?: string) =>
    fetchJSON<BulkOperation[]>(`/bulk?workspace_id=${workspaceId ?? ''}`),
  listActivity: (productId?: string) =>
    fetchJSON<ActivityEntry[]>(`/activity${productId ? `?product_id=${productId}` : ''}`),
  listNotifications: (unread = false) =>
    fetchJSON<{ id: string; title: string; body?: string }[]>(`/notifications?unread=${unread}`),
  adminHealth: () => fetchJSON<Record<string, unknown>>('/admin/health'),
  diagnostics: () => fetchJSON<Record<string, unknown>>('/diagnostics'),
  rebuildIndex: () => fetchJSON<{ indexed: number }>('/index/rebuild', { method: 'POST' }),
};

export const PRODUCT_STATUSES = ['draft', 'active', 'inactive', 'review', 'suppressed'] as const;
export const BULK_ACTIONS = ['publish', 'recalculate_quality', 'delete', 'update_status'] as const;
